import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChatMessage from './ChatMessage';
import type { ChatEchange, ChatMessageItem } from './chat.types';
import {
  CHAT_LONGUEUR_MAX,
  ChatError,
  chatbotEstActif,
  envoyerMessage,
  nouvelIdentifiantConversation,
} from '../../services/chatService';
import './Chat.css';

const MESSAGE_ACCUEIL =
  "Bonjour. Posez-moi une question sur Centralis, je réponds à partir de la documentation.";

/** Affiche quand un administrateur a coupe l'assistant depuis le back-office. */
const MESSAGE_INDISPONIBLE =
  "L'assistant est momentanément indisponible. Merci de réessayer plus tard.";

const idMessage = (): string => `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * L'assistant d'aide : une bulle en bas a droite, un panneau de conversation.
 *
 * LE FIL VIT DANS CET ONGLET. Il est garde dans l'etat React, pas dans `localStorage` :
 * fermer l'onglet efface la conversation a l'ecran et rien ne la recharge — un chatbot
 * d'aide se consulte, l'utilisateur ne le relit pas. Cote serveur, chaque question est
 * journalisee avec l'identifiant de fil genere ici, ce qui permet aux administrateurs
 * de relire une conversation entiere en back-office.
 *
 * UN CHAMP TEXTE, RIEN D'AUTRE. Pas d'envoi de fichier ni de piece jointe : la
 * documentation est alimentee cote serveur, depuis le back-office. C'est ce qui permet
 * de garantir que le modele ne voit que ce qui a ete choisi.
 */
const ChatWidget = () => {
  // `null` tant que la reponse du serveur n'est pas arrivee : on n'affiche rien avant
  // de savoir, plutot que de faire clignoter la pastille puis la retirer.
  const [actif, setActif] = useState<boolean | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [saisie, setSaisie] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  // Un identifiant par vie du widget : il suit les questions de cet onglet et meurt
  // avec lui, exactement comme le fil qu'il designe.
  const [conversationId] = useState(nouvelIdentifiantConversation);

  const finDuFilRef = useRef<HTMLDivElement>(null);
  const champRef = useRef<HTMLTextAreaElement>(null);
  const requeteRef = useRef<AbortController | null>(null);

  // React.StrictMode monte les effets deux fois en developpement : sans cette
  // annulation, une question partirait en double a chaque envoi.
  useEffect(() => () => requeteRef.current?.abort(), []);

  // L'interrupteur du back-office : une seule interrogation au montage. Inutile de le
  // surveiller en continu — une coupure en cours de session sera signalee par le
  // serveur au prochain envoi, avec son propre message.
  useEffect(() => {
    const controleur = new AbortController();

    void chatbotEstActif(controleur.signal).then(setActif);

    return () => controleur.abort();
  }, []);

  useEffect(() => {
    if (ouvert) {
      finDuFilRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, ouvert, envoiEnCours]);

  useEffect(() => {
    if (ouvert) {
      champRef.current?.focus();
    }
  }, [ouvert]);

  const fermer = useCallback(() => setOuvert(false), []);

  // Echap ferme le panneau : c'est le reflexe attendu, et le widget flotte au-dessus
  // du contenu sans le bloquer.
  useEffect(() => {
    if (!ouvert) return undefined;

    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer();
    };

    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [ouvert, fermer]);

  const envoyer = useCallback(async () => {
    const question = saisie.trim();
    if (question === '' || envoiEnCours) return;

    const historique: ChatEchange[] = messages
      .filter((m) => !m.erreur)
      .map((m) => ({ role: m.role, contenu: m.contenu }));

    setMessages((precedents) => [
      ...precedents,
      { id: idMessage(), role: 'user', contenu: question },
    ]);
    setSaisie('');
    setEnvoiEnCours(true);

    requeteRef.current?.abort();
    const controleur = new AbortController();
    requeteRef.current = controleur;

    try {
      const reponse = await envoyerMessage(question, historique, conversationId, controleur.signal);

      setMessages((precedents) => [
        ...precedents,
        {
          id: idMessage(),
          role: 'assistant',
          contenu: reponse.reponse,
          sourceTrouvee: reponse.source_trouvee,
        },
      ]);
    } catch (erreur) {
      if (controleur.signal.aborted) return;

      setMessages((precedents) => [
        ...precedents,
        {
          id: idMessage(),
          role: 'assistant',
          erreur: true,
          contenu:
            erreur instanceof ChatError
              ? erreur.message
              : "L'assistant est momentanément indisponible. Réessayez dans un instant.",
        },
      ]);
    } finally {
      setEnvoiEnCours(false);
    }
  }, [saisie, envoiEnCours, messages, conversationId]);

  const surTouche = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Entree envoie, Maj+Entree passe a la ligne : convention attendue d'un chat.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void envoyer();
    }
  };

  // Coupe en back-office, le widget RESTE visible : le retirer laisserait croire a une
  // panne du site, la ou un message assume dit que l'aide reviendra. `null` (etat pas
  // encore connu) se comporte comme disponible — l'interrogation arrive en quelques
  // dizaines de millisecondes, et le serveur refuserait de toute facon avec son propre
  // message si la coupure etait effective.
  const indisponible = actif === false;

  if (!ouvert) {
    return (
      <button
        type="button"
        className="centralis-chat__pastille"
        onClick={() => setOuvert(true)}
        aria-label="Ouvrir l'assistant d'aide"
        title="Besoin d'aide ?"
      >
        <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M12 3c-4.97 0-9 3.36-9 7.5 0 2.32 1.27 4.4 3.26 5.77-.12 1.03-.5 2.2-1.4 3.32a.5.5 0 0 0 .5.8c2.2-.4 3.77-1.3 4.72-2 .62.1 1.26.16 1.92.16 4.97 0 9-3.36 9-7.5S16.97 3 12 3Z"
          />
        </svg>
      </button>
    );
  }

  return (
    <section className="centralis-chat" role="dialog" aria-label="Assistant d'aide Centralis">
      <header className="centralis-chat__entete">
        <div>
          <h2 className="centralis-chat__titre">Assistant IA Centralis</h2>
          {/* La mention est sous le TITRE, vue des l'ouverture : elle cadre toute la
              conversation qui suit, au lieu de n'apparaitre qu'au moment d'envoyer. */}
          <p className="centralis-chat__sous-titre">L'IA peut faire des erreurs.</p>
        </div>
        <button
          type="button"
          className="centralis-chat__fermer"
          onClick={fermer}
          aria-label="Fermer l'assistant"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              d="M6 6l12 12M18 6L6 18"
            />
          </svg>
        </button>
      </header>

      <div className="centralis-chat__fil">
        {indisponible && (
          <p className="centralis-chat__indisponible" role="status">
            {MESSAGE_INDISPONIBLE}
          </p>
        )}

        {!indisponible && messages.length === 0 && (
          <p className="centralis-chat__accueil">{MESSAGE_ACCUEIL}</p>
        )}

        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {envoiEnCours && (
          <div className="centralis-chat__attente" role="status">
            <span className="centralis-chat__rotateur" aria-hidden="true" />
            L'assistant cherche dans la documentation...
          </div>
        )}

        <div ref={finDuFilRef} />
      </div>

      {!indisponible && (
        <div className="centralis-chat__saisie">
          <textarea
            ref={champRef}
            className="centralis-chat__champ"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value.slice(0, CHAT_LONGUEUR_MAX))}
            onKeyDown={surTouche}
            placeholder="Votre question..."
            maxLength={CHAT_LONGUEUR_MAX}
            rows={2}
            disabled={envoiEnCours}
            aria-label="Votre question"
          />
          <div className="centralis-chat__barre">
            <span className="centralis-chat__compteur">
              {saisie.length} / {CHAT_LONGUEUR_MAX}
            </span>
            <button
              type="button"
              className="centralis-chat__envoyer"
              onClick={() => void envoyer()}
              disabled={envoiEnCours || saisie.trim() === ''}
            >
              {envoiEnCours ? (
                <>
                  <span className="centralis-chat__rotateur centralis-chat__rotateur--bouton" aria-hidden="true" />
                  Envoi
                </>
              ) : (
                'Envoyer'
            )}
          </button>
        </div>
      </div>
      )}
    </section>
  );
};

export default ChatWidget;
